// ===============================================================
// Forms — Template List
// Browse, search, filter, and manage form templates
// ===============================================================

import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  SimpleGrid, ActionIcon, Stack, Loader, Center,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconCopy, IconTrash, IconTemplate,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useFormsStore } from '../../store/formsStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { primary } from '../../lib/colors';

export default function FormTemplates() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { canEdit } = useModulePermission('forms');
  const { templates, loading, loadTemplates, deleteTemplate } = useFormsStore();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(searchParams.get('status') || 'all');

  useEffect(() => {
    loadTemplates();
  }, []);

  const filtered = useMemo(() => {
    return templates.filter(t => {
      const matchesSearch = !search ||
        t.templateName.toLowerCase().includes(search.toLowerCase()) ||
        (t.description || '').toLowerCase().includes(search.toLowerCase());
      const matchesStatus = !statusFilter || statusFilter === 'all' || t.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [templates, search, statusFilter]);

  const getFieldCount = (t: { schema?: string; schemaJson?: string }) => {
    const raw = (t as any).schemaJson || t.schema || '';
    if (!raw) return 0;
    try {
      const parsed = JSON.parse(raw);
      // V2 schema uses pages[].fields[]
      if (Array.isArray(parsed.pages)) {
        return parsed.pages.reduce((sum: number, p: any) => sum + (Array.isArray(p.fields) ? p.fields.length : 0), 0);
      }
      return Array.isArray(parsed.fields) ? parsed.fields.length : 0;
    } catch {
      return 0;
    }
  };

  const statusColor = (s: string) => {
    switch (s) {
      case 'published': return 'green';
      case 'draft': return 'yellow';
      case 'archived': return 'gray';
      default: return 'gray';
    }
  };

  if (loading) {
    return <Center p="xl"><Loader /></Center>;
  }

  return (
    <div>
      {/* Header */}
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Form Templates</Title>
        {canEdit && (
          <Button
            leftSection={<IconPlus size={14} />}
            size="sm"
            color={primary[700]}
            onClick={() => navigate('/forms/builder')}
          >
            New Template
          </Button>
        )}
      </Group>

      {/* Search + Filter */}
      <Group gap="sm" mb="md">
        <TextInput
          placeholder="Search templates..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1 }}
        />
        <Select
          data={[
            { value: 'all', label: 'All Statuses' },
            { value: 'draft', label: 'Draft' },
            { value: 'published', label: 'Published' },
            { value: 'archived', label: 'Archived' },
          ]}
          value={statusFilter}
          onChange={setStatusFilter}
          w={160}
        />
      </Group>

      {/* Template Grid */}
      {filtered.length === 0 && !canEdit ? (
        <Paper p="xl" radius="sm" withBorder ta="center">
          <IconTemplate size={40} color="#939598" style={{ marginBottom: 8 }} />
          <Text size="sm" c="dimmed">No form templates found</Text>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
          {filtered.map(t => {
            const fieldCount = getFieldCount(t);
            return (
              <Paper
                key={t.id}
                p="md"
                radius="sm"
                withBorder
                style={{ cursor: 'pointer', position: 'relative' }}
                onClick={() => navigate(`/forms/builder/${t.id}`)}
                className="hover-card"
              >
                <Stack gap="xs">
                  <Group justify="space-between" align="flex-start">
                    <Text fw={600} size="sm" lineClamp={1}>{t.templateName}</Text>
                    <Badge size="xs" color={statusColor(t.status)}>{t.status}</Badge>
                  </Group>

                  {t.description && (
                    <Text size="xs" c="dimmed" lineClamp={2}>{t.description}</Text>
                  )}

                  <Group gap="xs">
                    <Badge size="xs" variant="light" color="#939598">{fieldCount} fields</Badge>
                    <Badge size="xs" variant="light" color="var(--color-primary-700)">{t.responseCount || 0} responses</Badge>
                    {t.module && (
                      <Badge size="xs" variant="light" color="var(--data-viz-650)">{t.module}</Badge>
                    )}
                    {t.isPublic === 'true' && t.publicSlug && t.status === 'published' && (
                      <Badge size="xs" variant="light" color="#03AC9E" style={{ cursor: 'pointer' }}
                        onClick={e => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(`${window.location.origin}/forms/public/${t.publicSlug}`);
                          notifications.show({ message: 'Public link copied!', color: 'var(--color-success)' });
                        }}>
                        Copy Link
                      </Badge>
                    )}
                    {t.isPublic === 'true' && !t.publicSlug && (
                      <Badge size="xs" variant="light" color="#03AC9E">Public</Badge>
                    )}
                  </Group>

                  {canEdit && (
                    <Group gap="xs" justify="flex-end" mt={4}>
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        color="#939598"
                        title="Duplicate"
                        onClick={e => {
                          e.stopPropagation();
                          // Duplicate will be handled by the builder
                          navigate(`/forms/builder?duplicate=${t.id}`);
                        }}
                      >
                        <IconCopy size={14} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        color="var(--color-error)"
                        title="Delete"
                        onClick={e => {
                          e.stopPropagation();
                          if (confirm(`Delete "${t.templateName}"?`)) {
                            deleteTemplate(t.id);
                          }
                        }}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  )}
                </Stack>
              </Paper>
            );
          })}

          {/* Create New card */}
          {canEdit && (
            <Paper
              p="md"
              radius="sm"
              style={{
                cursor: 'pointer',
                border: '2px dashed #939598',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 140,
              }}
              onClick={() => navigate('/forms/builder')}
            >
              <Stack align="center" gap="xs">
                <IconPlus size={24} color="#939598" />
                <Text size="sm" c="dimmed">Create New Template</Text>
              </Stack>
            </Paper>
          )}
        </SimpleGrid>
      )}

      {/* Empty state when no templates exist at all */}
      {templates.length === 0 && canEdit && (
        <Paper p="xl" radius="sm" withBorder ta="center" mt="md">
          <IconTemplate size={40} color="#939598" style={{ marginBottom: 8 }} />
          <Text fw={600} mb={4}>Create your first form template</Text>
          <Text size="sm" c="dimmed" mb="md">
            Build custom forms to collect data from your team or the public.
          </Text>
          <Button
            leftSection={<IconPlus size={14} />}
            color={primary[700]}
            onClick={() => navigate('/forms/builder')}
          >
            Create Template
          </Button>
        </Paper>
      )}

      {/* Read-only fallback for viewers when no templates exist */}
      {templates.length === 0 && !canEdit && (
        <Paper p="xl" radius="sm" withBorder ta="center" mt="md">
          <IconTemplate size={40} color="#939598" style={{ marginBottom: 8 }} />
          <Text fw={600} mb={4}>No form templates yet</Text>
          <Text size="sm" c="dimmed">
            Templates haven't been created in this workspace yet. Contact your
            administrator if you need a form built.
          </Text>
        </Paper>
      )}
    </div>
  );
}
